import axios from 'axios'
import { useToast } from 'vue-toastification'
import { config } from '../config/app'
import { clearSessionCompletely } from '../utils/userSessionCache'

const toast = useToast()

// Flag para controlar se estamos fazendo logout
let isLoggingOut = false

// 🔥 Flag para evitar múltiplos logouts simultâneos ao apertar F5 várias vezes
let logoutInProgress = false

// 🔥 Timeout para resetar o flag de logout
let logoutTimeout = null

// 🔥 Contador de erros 401 em sequência (proteção extra contra F5 múltiplo)
let consecutive401Count = 0
let reset401Timeout = null

// 🔥 Timestamp da última requisição bem-sucedida
let lastSuccessfulRequest = Date.now()

const clearSessionAndRedirect = async (reason) => {
  if (logoutInProgress) {
    return
  }

  logoutInProgress = true
  setLoggingOut(true)

  console.warn('[API] ⚠️ Sessão inválida - limpando credenciais:', reason)

  try {
    await clearSessionCompletely()
  } catch (e) {
    console.warn('[API] Erro ao limpar caches de sessão:', e)
  }

  localStorage.removeItem('token')
  localStorage.removeItem('user')
  delete api.defaults.headers.common['Authorization']

  if (logoutTimeout) {
    clearTimeout(logoutTimeout)
  }

  logoutTimeout = setTimeout(() => {
    logoutInProgress = false
    setLoggingOut(false)
  }, 1000)

  if (window.location.pathname !== '/login') {
    window.location.replace('/login')
  }
}

// Função para definir o estado de logout
export const setLoggingOut = (value) => {
  isLoggingOut = value
  console.log('[API] 🔄 isLoggingOut setado para:', value)
}

export const getIsLoggingOut = () => isLoggingOut

// Configuração base da API (VITE_BACKEND_URL ou /api em dev via config)
const api = axios.create({
  baseURL: config.backendUrl,
  timeout: 30000, // 30 segundos para operações que podem demorar mais
  headers: {
    'Content-Type': 'application/json',
  },
})

console.log('🔧 API Configurada com URL:', api.defaults.baseURL)

// Interceptor para requisições
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    console.error('[API] ❌ Erro no interceptor de requisição:', error)
    return Promise.reject(error)
  }
)

// Interceptor para respostas
api.interceptors.response.use(
  (response) => {
    // 🔥 Atualizar timestamp de sucesso e resetar contador de 401s
    lastSuccessfulRequest = Date.now()
    consecutive401Count = 0
    
    // Resposta bem-sucedida, apenas retornar
    return response
  },
  (error) => {
    // 🔥 IGNORAR requisições canceladas (não são erros reais)
    if (axios.isCancel(error)) {
      console.log('[API] ℹ️ Requisição cancelada (navegação rápida ou abort):', error.config?.url)
      return Promise.reject(error)
    }
    
    // 🔥 IGNORAR erros de rede durante navegação
    if (error.code === 'ERR_CANCELED') {
      console.log('[API] ℹ️ Requisição cancelada pelo navegador:', error.config?.url)
      return Promise.reject(error)
    }
    
    const status = error.response?.status
    const url = error.config?.url || ''
    const isWhatsAppConnectionLookup =
      status === 404 &&
      error.config?.method?.toLowerCase() === 'get' &&
      /\/whatsapp\/workspace\/\d+\/?$/.test(url.split('?')[0])

    if (isWhatsAppConnectionLookup) {
      console.log('[API] ℹ️ WhatsApp sem conexão cadastrada:', url)
    } else {
      console.error('[API] ❌ Erro na resposta:', status, url)
    }

    // Erro 401 - Token inválido ou expirado
    if (error.response?.status === 401) {
      const hasToken = !!localStorage.getItem('token')
      
      console.log('[API] ℹ️ Erro 401 recebido em:', url)

      // Evitar auto logout em endpoints públicos
      const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/register')

      if (hasToken && !isAuthEndpoint && !isLoggingOut) {
        clearSessionAndRedirect(`401 em ${url || 'endpoint desconhecido'}`)
      } else {
        console.log('[API] ℹ️ 401 sem ação automática (sem token ou endpoint público)')
      }

      return Promise.reject(error)
    } 
    // Erro 403 - Acesso negado
    else if (error.response?.status === 403) {
      const backendMsg = error.response?.data?.error
      toast.error(backendMsg || 'Você não tem permissão para acessar este recurso.')
    } 
    // 🔥 Erros 4xx (exceto 401 e 403) - NÃO mostrar toast aqui
    // Deixar que o componente trate (são erros de validação, not found, etc)
    else if (error.response?.status >= 400 && error.response?.status < 500) {
      console.log('[API] ℹ️ Erro 4xx - será tratado pelo componente:', error.response?.status, error.config?.url)
      // Não mostrar toast - deixar o componente decidir
    }
    // 🔥 Erros 5xx - Servidor - NÃO mostrar toast aqui também
    // Os stores/componentes já mostram mensagens mais específicas (ex: "Erro ao criar workspace")
    // Evita duplicação de toasts
    else if (error.response?.status >= 500) {
      console.error('[API] ❌ Erro do servidor:', error.response?.status, error.response?.data)
      console.error('[API] ❌ URL:', error.config?.url)
      // Não mostrar toast - deixar o store/componente mostrar mensagem específica
    } 
    // Timeout
    else if (error.code === 'ECONNABORTED') {
      console.error('[API] ❌ Timeout na requisição:', error.config?.url)
      toast.error('A requisição demorou muito. Tente novamente.')
    } 
    // Sem resposta - Problema de conexão REAL (servidor offline)
    else if (!error.response && error.code !== 'ERR_CANCELED') {
      console.error('[API] ❌ Erro de conexão (servidor pode estar offline):', error.message)
      toast.error('Erro de conexão. Verifique se o servidor está rodando.')
    }
    
    return Promise.reject(error)
  }
)

/** Timeout maior para envio WhatsApp (gateway + fila Baileys) */
export const WHATSAPP_SEND_TIMEOUT_MS = 60000

export function postWhatsAppSend(url, data) {
  return api.post(url, data, { timeout: WHATSAPP_SEND_TIMEOUT_MS })
}

export default api
